import {FC, Fragment, useCallback, useState} from "react";
import {useDomainIDParam} from "../types";
import BackendError from "./BackendError";
import {
    Button,
    Checkbox,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableRow,
    TextField,
    useTheme,
} from "@mui/material";
import {useAdminListQuery, useRevokeAdminRolesMutation, useAddAdminRoleMutation} from "./queries.generated";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {defineMessage, FormattedMessage} from "react-intl";
import FormattedTableHead from "./FormattedTableHead";
import {Page} from "./pages";
import {
    FormDialogActionButtons,
    DialogActions,
    DialogContent,
    FormattedDialogTitle,
    ConfirmDialog,
    useBoolState,
    ThreeDots,
    FormContainer,
    FormattedFormRow,
    MultipleLineError,
} from "@dvrt/graphql-widgets";
import {commonTranslations} from "@/utils/translations";
import {useForm} from "@/utils/Form";
import {formPagesWidthDialog} from "@/constants";
import {useDefaultLanguage} from "@/hooks";
import {useAvailableTranslationsQuery} from "./domain_settings/bulk_extension/bulkextensionqueries.generated";
import LanguageSelector from "./LanguageSelector";

function RevokeAdminButton(props: {domainId: number; userSubs: string[]; onRevoke: () => void}) {
    const [open, setOpen, setClose] = useBoolState();

    const {domainId, userSubs, onRevoke} = props;

    const [revokeAdminRoles] = useRevokeAdminRolesMutation();

    async function confirm() {
        await revokeAdminRoles({variables: {domainId, userSubs}});
        setClose();
        onRevoke();
    }

    return (
        <Fragment>
            <Button variant="text" color="secondary" size="small" onClick={setOpen}>
                <FormattedMessage id="revoke" defaultMessage="Revoke" />
            </Button>
            <ConfirmDialog
                message={defineMessage({
                    id: "admin.revokeConfirm",
                    defaultMessage: "Do you really want to revoke the domain admin role?",
                })}
                onConfirm={confirm}
                isOpen={open}
                close={setClose}
                buttonIdPrefix="revoke-admin-role"
            />
        </Fragment>
    );
}

type InferQueryVariables<T> = T extends (baseOptions?: {variables?: infer V}) => any ? V : never;

type AddAdminDialogContentProps = {
    refetch: () => void;
    close: () => void;
    initialData: InferQueryVariables<typeof useAddAdminRoleMutation>;
    availableTranslations?: string[];
};

const AddAdminDialogContent: FC<AddAdminDialogContentProps> = ({
    refetch,
    close,
    initialData,
    availableTranslations,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const form = useForm(useAddAdminRoleMutation, "addAdminRole", initialData);
    const canSubmit = !submitting && form.values.email.length > 0;

    const submit = useCallback(async () => {
        setSubmitting(true);
        if (await form.submit()) {
            form.reset();
            refetch();
            close();
        }
        setSubmitting(false);
    }, [close, form, refetch]);

    return (
        <>
            <DialogContent>
                <FormContainer style={{width: formPagesWidthDialog}}>
                    <FormattedFormRow label={commonTranslations.email} required>
                        <TextField
                            variant="standard"
                            fullWidth
                            margin="dense"
                            type="text"
                            value={form.values.email}
                            onChange={(event) => form.updateValues({email: event.target.value})}
                            error={form.errorHelper.hasError("email")}
                            helperText={
                                <MultipleLineError
                                    errors={form.errorHelper.fieldErrors("email", {removePrefix: true})}
                                />
                            }
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={commonTranslations.canCreateEndpoints} required>
                        <Checkbox
                            checked={form.values.canCreateEndpoints}
                            onChange={(event) => form.updateValues({canCreateEndpoints: event.target.checked})}
                            color="primary"
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={commonTranslations.language} required>
                        <LanguageSelector
                            value={form.values.language}
                            onChange={(language) => form.updateValues({language})}
                            variant="standard"
                            languages={availableTranslations || []}
                        />
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <FormDialogActionButtons
                    mainAction={submit}
                    mainButtonEnabled={canSubmit}
                    secondaryAction={() => form.reset()}
                    secondaryButtonEnabled={form.hasChanged}
                    cancel={close}
                    cancelDisabled={submitting}
                />
            </DialogActions>
        </>
    );
};

type AddAdminDialogProps = {
    isOpen: boolean;
    close: () => void;
    refetch: () => void;
    domainId: number;
};

const AddAdminDialog: FC<AddAdminDialogProps> = ({isOpen, close, refetch, domainId}) => {
    const {data: translationsData, loading: translationsLoading} = useAvailableTranslationsQuery();
    const {defaultLanguage, loading: defaultLanguageLoading} = useDefaultLanguage({domainId});

    const loading = translationsLoading || defaultLanguageLoading;

    return (
        <Dialog open={isOpen} maxWidth="lg">
            <FormattedDialogTitle title={defineMessage({id: "admin.add", defaultMessage: "Add domain admin"})} />
            {loading ? (
                <ThreeDots height={5} />
            ) : (
                <AddAdminDialogContent
                    refetch={refetch}
                    close={close}
                    initialData={{domainId, email: "", canCreateEndpoints: false, language: defaultLanguage}}
                    availableTranslations={translationsData?.availableTranslations}
                />
            )}
        </Dialog>
    );
};

const AdminList = () => {
    const domainId = useDomainIDParam();

    const theme = useTheme();

    const {loading, error, data, refetch} = useAdminListQuery({variables: {domainId}});

    const [open, setOpen] = useState(false);

    const [selectedSubs, setSelectedSubs] = useState<string[]>([]);

    if (loading)
        return (
            <div style={{padding: 20}}>
                <ThreeDots height={5} />
            </div>
        );

    if (error)
        return (
            <div style={{padding: 20}}>
                <BackendError error={error} />
            </div>
        );

    const addButton = (
        <Button
            size="small"
            onClick={() => setOpen(true)}
            variant="contained"
            color="primary"
            style={{whiteSpace: "nowrap"}}
        >
            <FormattedMessage id="admin.add" defaultMessage="Add domain admin" />
        </Button>
    );

    return (
        <Page
            title={defineMessage({id: "admin.title", defaultMessage: "Domain admin list"})}
            controls={addButton}
            inlineControls
        >
            <AddAdminDialog domainId={domainId} refetch={refetch} close={() => setOpen(false)} isOpen={open} />
            <Table>
                <FormattedTableHead
                    cells={[
                        {},
                        {
                            label: defineMessage({id: "name", defaultMessage: "Name"}),
                        },
                        {
                            label: defineMessage({id: "admin.emails", defaultMessage: "Emails"}),
                        },
                        {
                            label: defineMessage({id: "admin.knownAliases", defaultMessage: "Known aliases"}),
                        },
                        {
                            label: defineMessage({
                                id: "admin.canCreateEndpoint",
                                defaultMessage: "Can create endpoints",
                            }),
                            props: {align: "center", style: {whiteSpace: "nowrap"}},
                        },
                        {},
                    ]}
                />
                <TableBody>
                    {data.admins.map((admin) => (
                        <TableRow key={admin.sub}>
                            <TableCell>
                                <Checkbox
                                    style={{padding: 0}}
                                    checked={selectedSubs.indexOf(admin.sub) !== -1}
                                    onChange={() => {
                                        if (selectedSubs.indexOf(admin.sub) !== -1)
                                            setSelectedSubs(selectedSubs.filter((s) => s !== admin.sub));
                                        else setSelectedSubs(selectedSubs.concat([admin.sub]));
                                    }}
                                    color="primary"
                                />
                            </TableCell>
                            <TableCell>{admin.name}</TableCell>
                            <TableCell>{admin.emails.join(", ")}</TableCell>
                            <TableCell>{admin.aliases.join(", ")}</TableCell>
                            <TableCell style={{textAlign: "center"}}>
                                {admin.canCreateEndpoints ? (
                                    <FontAwesomeIcon style={{color: theme.palette.primary.main}} icon="check-circle" />
                                ) : (
                                    <FontAwesomeIcon
                                        style={{color: theme.palette.secondary.main}}
                                        icon="times-circle"
                                    />
                                )}
                            </TableCell>
                            <TableCell style={{padding: 0}}>
                                <RevokeAdminButton
                                    domainId={domainId}
                                    userSubs={[admin.sub]}
                                    onRevoke={() => {
                                        setSelectedSubs(selectedSubs.filter((s) => s !== admin.sub));
                                        refetch();
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                    {selectedSubs.length ? (
                        <TableRow>
                            <TableCell colSpan={100}>
                                <RevokeAdminButton
                                    domainId={domainId}
                                    userSubs={selectedSubs}
                                    onRevoke={() => {
                                        setSelectedSubs([]);
                                        refetch();
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableFooter>
            </Table>
        </Page>
    );
};

export default AdminList;
